import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ConversarionType,
  MessageType,
  UnreadCountsType,
} from "types/loadedData";

type UnreadRoleType = keyof UnreadCountsType;

interface ChatState {
  chat: ConversarionType | null;
  conversations: ConversarionType[];
  messages: MessageType[];
  messageCount: number;
}

const defaultUnreadCounts: UnreadCountsType = {
  customer: 0,
  staff: 0,
  admin: 0,
};

const initialState: ChatState = {
  chat: null,
  conversations: [],
  messages: [],
  messageCount: 0,
};

const normalizeUnreadCounts = (
  unreadCounts?: Partial<UnreadCountsType> | null
): UnreadCountsType => ({
  ...defaultUnreadCounts,
  ...(unreadCounts || {}),
});

const normalizeMessage = (message?: MessageType | null): MessageType | null => {
  if (!message) return null;

  return {
    ...message,
    status: message.status || "sent",
  };
};

const normalizeConversation = (
  conversation?: Partial<ConversarionType> | null
): ConversarionType | null => {
  if (!conversation?._id) return null;

  return {
    ...conversation,
    unreadCounts: normalizeUnreadCounts(conversation.unreadCounts),
    lastMessage: normalizeMessage(conversation.lastMessage),
  } as ConversarionType;
};

const getMessageIdentifier = (message: Partial<MessageType>) =>
  message.clientMessageId ||
  message._id ||
  `${message.conversation || ""}-${message.timestamp || ""}`;

const moveConversationToFront = (
  conversations: ConversarionType[],
  conversation: ConversarionType
) => [
  conversation,
  ...conversations.filter((item) => item._id !== conversation._id),
];

export const chat = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setChat: (state, action: PayloadAction<ConversarionType | null>) => {
      state.chat = normalizeConversation(action.payload);
    },
    setConversations: (state, action: PayloadAction<ConversarionType[]>) => {
      state.conversations = action.payload
        .map((conversation) => normalizeConversation(conversation))
        .filter(Boolean) as ConversarionType[];
    },
    upsertConversation: (
      state,
      action: PayloadAction<
        Partial<ConversarionType> & { _id: string; moveToTop?: boolean }
      >
    ) => {
      const existingConversation = state.conversations.find(
        (conversation) => conversation._id === action.payload._id
      );
      const mergedConversation = normalizeConversation({
        ...existingConversation,
        ...action.payload,
        unreadCounts: normalizeUnreadCounts(
          action.payload.unreadCounts || existingConversation?.unreadCounts
        ),
        lastMessage:
          normalizeMessage(action.payload.lastMessage) ||
          normalizeMessage(existingConversation?.lastMessage),
      });

      if (!mergedConversation) return;

      state.conversations =
        action.payload.moveToTop === false
          ? state.conversations.map((conversation) =>
              conversation._id === mergedConversation._id
                ? mergedConversation
                : conversation
            )
          : moveConversationToFront(state.conversations, mergedConversation);

      if (state.chat?._id === mergedConversation._id) {
        state.chat = normalizeConversation({
          ...state.chat,
          ...mergedConversation,
        });
      }
    },
    setMessages: (state, action: PayloadAction<MessageType[]>) => {
      state.messages = action.payload.map(
        (message) => normalizeMessage(message) as MessageType
      );
    },
    upsertMessage: (state, action: PayloadAction<MessageType>) => {
      const incomingMessage = normalizeMessage(action.payload) as MessageType;
      const incomingId = getMessageIdentifier(incomingMessage);
      const messageIndex = state.messages.findIndex(
        (message) => getMessageIdentifier(message) === incomingId
      );

      if (messageIndex >= 0) {
        state.messages[messageIndex] = {
          ...state.messages[messageIndex],
          ...incomingMessage,
        };
      } else {
        state.messages = [incomingMessage, ...state.messages];
      }

      if (incomingMessage.conversation) {
        const existingConversation =
          state.conversations.find(
            (conversation) => conversation._id === incomingMessage.conversation
          ) || state.chat;

        const mergedConversation = normalizeConversation({
          ...existingConversation,
          _id: incomingMessage.conversation,
          lastMessage: incomingMessage,
        });

        if (mergedConversation) {
          state.conversations = moveConversationToFront(
            state.conversations,
            mergedConversation
          );

          if (state.chat?._id === incomingMessage.conversation) {
            state.chat = normalizeConversation({
              ...state.chat,
              lastMessage: incomingMessage,
            });
          }
        }
      }
    },
    setMessageStatus: (
      state,
      action: PayloadAction<{
        clientMessageId: string;
        status: "sending" | "sent" | "failed";
      }>
    ) => {
      const targetMessage = state.messages.find(
        (message) => message.clientMessageId === action.payload.clientMessageId
      );

      if (targetMessage) {
        targetMessage.status = action.payload.status;
      }
    },
    removeMessage: (
      state,
      action: PayloadAction<{ messageId?: string; clientMessageId?: string }>
    ) => {
      const targetIndex = state.messages.findIndex((message) => {
        if (action.payload.messageId && message._id === action.payload.messageId) {
          return true;
        }

        if (
          action.payload.clientMessageId &&
          message.clientMessageId === action.payload.clientMessageId
        ) {
          return true;
        }

        return false;
      });

      if (targetIndex < 0) return;

      const removedMessage = state.messages[targetIndex];
      state.messages.splice(targetIndex, 1);
      state.messageCount = Math.max(0, state.messageCount - 1);

      if (
        state.chat?.lastMessage &&
        getMessageIdentifier(state.chat.lastMessage) ===
          getMessageIdentifier(removedMessage)
      ) {
        state.chat.lastMessage = state.messages[0] || null;
      }
    },
    setMessageCount: (state, action: PayloadAction<number>) => {
      state.messageCount = action.payload;
    },
    setConversationRead: (
      state,
      action: PayloadAction<{ conversationId: string; role: UnreadRoleType }>
    ) => {
      const targetConversation = state.conversations.find(
        (conversation) => conversation._id === action.payload.conversationId
      );

      if (targetConversation) {
        targetConversation.unreadCounts = {
          ...targetConversation.unreadCounts,
          [action.payload.role]: 0,
        };
      }

      if (state.chat?._id === action.payload.conversationId) {
        state.chat.unreadCounts = {
          ...state.chat.unreadCounts,
          [action.payload.role]: 0,
        };
      }
    },
    resetActiveThread: (state) => {
      state.chat = null;
      state.messages = [];
      state.messageCount = 0;
    },
  },
});

export const {
  setConversations,
  setChat,
  upsertConversation,
  setMessages,
  upsertMessage,
  setMessageStatus,
  removeMessage,
  setMessageCount,
  setConversationRead,
  resetActiveThread,
} = chat.actions;

export default chat.reducer;
